/**
 * usePermissions Composable
 * Centralized permission and role checking logic
 *
 * IMPORTANT: Role System Explained
 * ================================
 * There are TWO types of roles in this system:
 *
 * 1. GLOBAL ROLES (from auth.user.roles):
 *    - ROLE_ADMIN: System administrator with access to all companies
 *    These roles are NOT tied to any specific company.
 *
 * 2. COMPANY-SCOPED ROLES (from companyStore.keyUserTypes):
 *    - ROLE_COMPANY_ADMIN: Administrator for the CURRENTLY SELECTED company
 *    - ROLE_PROJECT_ADMIN: Project manager for projects in the CURRENTLY SELECTED company
 *    - ROLE_QUESTIONNAIRE_CREATOR: Can create questionnaires for the CURRENTLY SELECTED company
 *    - ROLE_KEY_ACCOUNT: Key account manager for the CURRENTLY SELECTED company
 *
 *    These roles are loaded from the selected company's keyUsers array and represent
 *    what the current user can do ONLY in the context of the currently selected company.
 *
 * When you switch companies (via CurrentCompany dropdown), the keyUserTypes changes to reflect
 * your roles in that specific company. This ensures proper permission isolation between companies.
 *
 * Usage:
 * import { usePermissions } from '@/composables/usePermissions';
 *
 * const { hasPermission, isAdmin, hasRole } = usePermissions();
 *
 * if (hasPermission('VIEW_DASHBOARD')) { ... }
 * if (isAdmin.value) { ... }
 */

import { computed } from "vue";
import { useAuthStore } from "@/stores";
import { useCompanyStore } from "@/stores/companyStore";
import { PERMISSIONS, ROLES, ROLE_GROUPS } from "@/constants";

export function usePermissions() {
  const authStore = useAuthStore();
  const companyStore = useCompanyStore();

  // Get user and role data
  const user = computed(() => authStore.user);

  // Global roles (e.g., ROLE_ADMIN)
  const userRoles = computed(() => user.value?.roles || []);

  // Company-scoped roles for CURRENTLY SELECTED company
  const keyUserTypes = computed(() => companyStore.keyUserTypes || []);

  /**
   * Check if user has a specific role
   *
   * CRITICAL LOGIC:
   * - GLOBAL ROLES (only ROLE_ADMIN): Checked from auth.user.roles
   * - COMPANY-SCOPED ROLES (all others): Checked ONLY from keyUserTypes for currently selected company
   *
   * This prevents mixing roles from different companies. For example, if a user is
   * ROLE_PROJECT_ADMIN in Company A but only ROLE_KEY_ACCOUNT in Company B,
   * when Company B is selected, they should NOT have PROJECT_ADMIN permissions.
   *
   * @param {string} role - Role constant (e.g., ROLES.ADMIN)
   * @returns {boolean}
   */
  const hasRole = (role) => {
    // Virtual role — no DB entry, checked via delegation records in auth store
    if (role === ROLES.TRANSLATOR) {
      return authStore.hasDelegation;
    }

    // Check if this is a global role (currently only ROLE_ADMIN)
    const isGlobalRole = ROLE_GROUPS.GLOBAL_ROLES.includes(role);

    if (isGlobalRole) {
      return userRoles.value.includes(role);
    } else {
      return keyUserTypes.value.includes(role);
    }
  };

  /**
   * Check if user has ANY of the provided roles
   * @param {string[]} roles - Array of role constants
   * @returns {boolean}
   */
  const hasAnyRole = (roles) => {
    if (!Array.isArray(roles)) return false;
    return roles.some((role) => hasRole(role));
  };

  /**
   * Check if user has ALL of the provided roles
   * @param {string[]} roles - Array of role constants
   * @returns {boolean}
   */
  const hasAllRoles = (roles) => {
    if (!Array.isArray(roles)) return false;
    return roles.every((role) => hasRole(role));
  };

  /**
   * Check if user has a specific permission
   * @param {string} permission - Permission constant (e.g., 'VIEW_DASHBOARD')
   * @returns {boolean}
   */
  const hasPermission = (permission) => {
    const allowedRoles = PERMISSIONS[permission];
    if (!allowedRoles || !Array.isArray(allowedRoles)) {
      console.warn(`Permission '${permission}' not found in PERMISSIONS`);
      return false;
    }
    return hasAnyRole(allowedRoles);
  };

  /**
   * Check if user has ANY of the provided permissions
   * @param {string[]} permissions - Array of permission constants
   * @returns {boolean}
   */
  const hasAnyPermission = (permissions) => {
    if (!Array.isArray(permissions)) return false;
    return permissions.some((permission) => hasPermission(permission));
  };

  /**
   * Check if user has ALL of the provided permissions
   * @param {string[]} permissions - Array of permission constants
   * @returns {boolean}
   */
  const hasAllPermissions = (permissions) => {
    if (!Array.isArray(permissions)) return false;
    return permissions.every((permission) => hasPermission(permission));
  };

  // Computed properties for specific roles (most commonly used)
  const isAdmin = computed(() => hasRole(ROLES.ADMIN));
  const isSystemAdmin = computed(() => hasRole(ROLES.ADMIN)); // Alias for isAdmin
  const isCompanyAdmin = computed(() => hasRole(ROLES.COMPANY_ADMIN));
  const isProjectAdmin = computed(() => hasRole(ROLES.PROJECT_ADMIN));
  const isKeyAccount = computed(() => hasRole(ROLES.KEY_ACCOUNT));
  const isQuestionnaireCreator = computed(() =>
    hasRole(ROLES.QUESTIONNAIRE_CREATOR),
  );
  const isTranslationManager = computed(() =>
    hasRole(ROLES.TRANSLATION_MANAGER),
  );
  const isTranslator = computed(() => hasRole(ROLES.TRANSLATOR));
  const isUser = computed(() => hasRole(ROLES.USER));

  // Computed properties for role groups
  const isAnyAdmin = computed(() => hasAnyRole(ROLE_GROUPS.ADMIN_ROLES));
  const isHighLevelAdmin = computed(() =>
    hasAnyRole(ROLE_GROUPS.HIGH_LEVEL_ADMIN),
  );
  const isProjectManager = computed(() =>
    hasAnyRole(ROLE_GROUPS.PROJECT_MANAGERS),
  );
  const isParticipant = computed(() =>
    hasAnyRole(ROLE_GROUPS.PARTICIPANT_ROLES),
  );

  /**
   * Check if user ONLY has participant roles (no admin roles)
   */
  const isOnlyParticipant = computed(
    () => isParticipant.value && !isAnyAdmin.value,
  );

  /**
   * Get redirect path based on user roles (for login redirect)
   *
   * IMPORTANT: This function is called during login/route initialization,
   * potentially BEFORE keyUserTypes are loaded from the selected company.
   * Therefore, we need to check auth.user.roles as a fallback to determine
   * the initial redirect, even though those roles may include roles from multiple companies.
   *
   * The actual permission checks on each page will use the proper hasRole() logic
   * which respects company isolation.
   *
   * @returns {string} - Route name
   */
  const getDefaultRoute = () => {
    // Check keyUserTypes first (if already loaded)
    if (keyUserTypes.value && keyUserTypes.value.length > 0) {
      if (
        keyUserTypes.value.some((role) =>
          ROLE_GROUPS.HIGH_LEVEL_ADMIN.includes(role),
        )
      ) {
        return "dashboard";
      }
      if (keyUserTypes.value.includes(ROLES.PROJECT_ADMIN)) {
        return "projects";
      }
      if (
        keyUserTypes.value.some((role) =>
          ROLE_GROUPS.PARTICIPANT_ROLES.includes(role),
        )
      ) {
        return "home";
      }
    }

    // Fallback to auth.user.roles if keyUserTypes not yet loaded (initial login)
    // This is ONLY for initial routing decision, not for permission enforcement
    if (userRoles.value && userRoles.value.length > 0) {
      // Check for ROLE_ADMIN (truly global role)
      if (userRoles.value.includes(ROLES.ADMIN)) {
        return "dashboard";
      }

      // For other roles in auth.user.roles, make best guess for initial redirect
      // The user will be on the correct page once CurrentCompany loads and sets keyUserTypes
      const roles = userRoles.value;

      if (roles.some((role) => ROLE_GROUPS.HIGH_LEVEL_ADMIN.includes(role))) {
        return "dashboard";
      }
      if (roles.includes(ROLES.PROJECT_ADMIN)) {
        return "projects";
      }
      if (roles.some((role) => ROLE_GROUPS.PARTICIPANT_ROLES.includes(role))) {
        return "home";
      }
    }

    // If no roles found at all, go to not-found
    return "not-found";
  };

  return {
    // Methods
    hasRole,
    hasAnyRole,
    hasAllRoles,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    getDefaultRoute,

    // Specific role checks (computed)
    isAdmin,
    isSystemAdmin, // Alias for isAdmin
    isCompanyAdmin,
    isProjectAdmin,
    isKeyAccount,
    isQuestionnaireCreator,
    isTranslationManager,
    isTranslator,
    isUser,

    // Role group checks (computed)
    isAnyAdmin,
    isHighLevelAdmin,
    isProjectManager,
    isParticipant,
    isOnlyParticipant,

    // Raw data (computed)
    user,
    userRoles,
    keyUserTypes,
  };
}
